import { useCallback, useLayoutEffect, useState } from 'react';

import { useGlobalEvent } from '@luna/events';

import { readReturnUrl, removeReturnUrl } from './ReturnUrlService';
import { useUnsafeNavigate } from '../useNavigate';

export type SaveReturnUrlOptions = {
    disableSaveOnMount?: boolean;
};

export type ReturnUrlOptions = {
    key: string;
    defaultUrl?: string;
};
type ReturnUrlOptionsWithDefaultUrl = {
    key: string;
    defaultUrl: string;
};

const SESSION_STORAGE_KEY = 'returnUrls';

type StorageValue = {
    url: string;
    timestamp: number;
};
type StorageMap = Record<string, StorageValue>;

function getStorageMap(): StorageMap {
    const item = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!item) {
        return {};
    }
    return JSON.parse(item) ?? {};
}

function saveStorageMap(map: StorageMap): void {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(map));
}

function saveStorageItem(key: string, url: string): void {
    saveStorageMap({
        ...getStorageMap(),
        [key]: {
            url,
            timestamp: Date.now(),
        },
    });
}

type UseSaveReturnUrl = {
    /**
     * Deletes url from the storage
     */
    reset: () => void;
    /**
     * Saves url to the storage
     *
     * @param returnUrl url to save; fallbacks to returnUrl in the url
     */
    save: (returnUrl?: string) => void;
};

const useSaveReturnUrl = (options: ReturnUrlOptions & SaveReturnUrlOptions): UseSaveReturnUrl => {
    useLayoutEffect(() => {
        if (options.disableSaveOnMount) {
            return;
        }
        const currentReturnUrl = readReturnUrl();
        if (!currentReturnUrl) {
            return;
        }
        saveStorageItem(options.key, currentReturnUrl);
    }, [options.key, options.disableSaveOnMount]);

    const reset = useCallback(() => {
        clearStorageAndUrl(options.key);
    }, [options.key]);

    const save = useCallback(
        (returnUrl?: string) => {
            const url = returnUrl ?? readReturnUrl();
            if (!url) {
                return;
            }
            saveStorageItem(options.key, url);
        },
        [options.key],
    );

    return {
        reset,
        save,
    };
};

function clearStorageAndUrl(key: string): void {
    removeReturnUrl();
    const map = getStorageMap();
    delete map[key];
    saveStorageMap(map);
}

type UseReadReturnUrl = {
    returnUrl: string | undefined;
    navigateToReturnUrl: () => boolean;
};
type UseReadReturnUrlWithDefaultUrl = {
    returnUrl: string;
    navigateToReturnUrl: () => boolean;
};

function getUrlFromQueryOrStorage({ key }: ReturnUrlOptions): string {
    const currentReturnUrl = readReturnUrl();
    if (currentReturnUrl) {
        return currentReturnUrl;
    }

    return getStorageMap()[key]?.url;
}

const useReadReturnUrl = (options: ReturnUrlOptions): UseReadReturnUrl => {
    const navigate = useUnsafeNavigate();

    const [returnUrl, setReturnUrl] = useState<string | undefined>(
        getUrlFromQueryOrStorage(options) ?? options.defaultUrl,
    );

    useGlobalEvent('storage', () => {
        setReturnUrl(getUrlFromQueryOrStorage(options) ?? options.defaultUrl);
    });

    const navigateToReturnUrl = useCallback((): boolean => {
        if (!returnUrl) {
            return false;
        }
        navigate(returnUrl);
        return true;
    }, [returnUrl]);

    return {
        returnUrl,
        navigateToReturnUrl,
    };
};

export type CreateReturnUrl = {
    useSaveReturnUrl: (saveOptions?: SaveReturnUrlOptions) => UseSaveReturnUrl;
    useReadReturnUrl: () => UseReadReturnUrl;
};
type CreateReturnUrlWithDefaultUrl = {
    useSaveReturnUrl: (saveOptions?: SaveReturnUrlOptions) => UseSaveReturnUrl;
    useReadReturnUrl: () => UseReadReturnUrlWithDefaultUrl;
};

export function createReturnUrl(
    options: ReturnUrlOptionsWithDefaultUrl,
): CreateReturnUrlWithDefaultUrl;
export function createReturnUrl(options: ReturnUrlOptions): CreateReturnUrl;

export function createReturnUrl(options: ReturnUrlOptions): CreateReturnUrl {
    return {
        useSaveReturnUrl: (saveOptions?: SaveReturnUrlOptions) =>
            useSaveReturnUrl({ ...options, ...(saveOptions || {}) }),
        useReadReturnUrl: () => useReadReturnUrl(options),
    };
}
